import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, FileText } from "lucide-react";
import SEO from "@/components/site/SEO";
import { getProjectStatus, getAgreementStatus } from "@/lib/api";

export default function ProjectSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("loading"); // loading | paid | unpaid
  const [info, setInfo] = useState(null);
  const sid = params.get("session_id");
  const isAgreement = params.get("agreement") === "1";

  useEffect(() => {
    if (!sid) { setState("unpaid"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        if (isAgreement) {
          const r = await getAgreementStatus(sid);
          if (r.billing_added) { setInfo(r); setState("paid"); return; }
        } else {
          const r = await getProjectStatus(sid);
          if (r.paid) { setState("paid"); return; }
        }
      } catch { /* keep polling briefly */ }
      tries += 1;
      if (tries < 8) setTimeout(poll, 1500);
      else setState("unpaid");
    };
    poll();
  }, [sid, isAgreement]);

  const firstCharge = info?.first_charge_date
    ? new Date(info.first_charge_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <div className="min-h-screen pt-36 pb-24 flex items-start justify-center" data-testid="project-success-page">
      <SEO title="Project Booked | Julius Mason" path="/start-project/success" noindex lang="en" />
      <div className="max-w-xl w-full mx-5 border border-border bg-card p-10 text-center">
        {state === "loading" && (
          <>
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-5" />
            <h1 className="font-display text-3xl">Confirming your details…</h1>
            <p className="text-muted-foreground mt-3">One moment while Stripe confirms everything.</p>
          </>
        )}
        {state === "paid" && isAgreement && (
          <>
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-5" />
            <h1 className="font-display text-4xl" data-testid="agreement-confirmed">Agreement signed. We're live.</h1>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Your agreement is signed and your billing details are on file — <b className="text-foreground">$0 charged today</b>.
              Your free 30 days start now: I build and launch your site, and your first charge only
              happens on <b className="text-foreground">{firstCharge}</b>.
            </p>
            <div className="mt-8 text-left border border-border p-6 space-y-3 text-sm">
              <p className="overline text-primary">What happens next</p>
              <p>1. Signed PDF agreement in your inbox (check email)</p>
              <p>2. Kickoff checklist within 1 business day</p>
              <p>3. I build, you review, we launch — all inside your free month</p>
              <p>4. First charge on {firstCharge} — cancel free anytime before</p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/agreement/${info.agreement_id}/pdf`}
                className="inline-flex items-center gap-2 border border-border px-6 py-3 font-medium hover:border-primary hover:text-primary transition" data-testid="agreement-pdf-link">
                <FileText className="w-4 h-4" /> Download my agreement
              </a>
              <Link to="/" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary/90 active:scale-95 transition" data-testid="success-home-cta">
                Back to the site <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}
        {state === "paid" && !isAgreement && (
          <>
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-5" />
            <h1 className="font-display text-4xl" data-testid="deposit-confirmed">Your project is booked.</h1>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Your deposit has been received. I'll email your kickoff checklist within one business day —
              then we build, you review, and we launch.
            </p>
            <div className="mt-8 text-left border border-border p-6 space-y-3 text-sm">
              <p className="overline text-primary">What happens next</p>
              <p>1. Kickoff checklist email within 1 business day</p>
              <p>2. Shared project space to track progress</p>
              <p>3. Design &amp; build on a private preview link</p>
              <p>4. Review, refine, launch</p>
            </div>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition" data-testid="success-home-cta">
              Back to the site <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {state === "unpaid" && (
          <>
            <XCircle className="w-10 h-10 text-destructive mx-auto mb-5" />
            <h1 className="font-display text-3xl">Not confirmed yet.</h1>
            <p className="text-muted-foreground mt-3">If you completed checkout, this can take a moment — check your email. Otherwise you can restart securely.</p>
            <Link to="/start-project" className="mt-8 inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary/90 transition" data-testid="success-retry-cta">
              Restart checkout <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
